import * as React from 'react';
import { Checkbox, ClearFormButton, Form, CustomSubmitButton, Select, DatePickerRange, CustomSubmitButtonType, TextInput } from 'soprasteria-ui-library';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { EmergencyTreatmentSearchApi } from '../../../api/EmergencyTreatmentSearchApi';
import { useAuth } from '../../../App';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { ICareProvider } from '../../../models/ICareProvider';
import { ISearchEmergencyTreatmentReport } from "../../../models/IEmergencyTreatment"
import { LIBRETTOADMIN, VÅRDANSVARIG } from '../../../utils/RoleConstants';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

interface IEmergencyTreatmentSearchForm {
    preSetSearchParams?: ISearchEmergencyTreatmentReport;
    searchEmergencyTreatments: (data: ISearchEmergencyTreatmentReport) => void;
}

export const EmergencyTreatmentSearchForm: React.FunctionComponent<IEmergencyTreatmentSearchForm> = ({ preSetSearchParams, searchEmergencyTreatments }) => {

    const auth = useAuth();
    const api = new EmergencyTreatmentSearchApi(auth);
    const abortController = new AbortController();
    const userInfo = React.useContext(UserContext);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const [emergencyTreatmentStatuses, setEmergencyTreatmentStatuses] = React.useState<IListItem[]>();
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);
    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>();
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);
    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (userInfo.roles && userInfo.roles.length > 0) {
            getUmbrellaOrganisations();
            getEmergencyTreatmentStatuses();
        }

        return () => {
            abortController.abort();
        }
    }, [userInfo])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaCareProviderIdToUse;
            if (preSetSearchParams?.umbrellaCareProviderId) {
                umbrellaCareProviderIdToUse = parseInt(preSetSearchParams.umbrellaCareProviderId.toString());
                setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }
            else if (!hasRoleSystemAdministrator) {
                umbrellaCareProviderIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse);
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);

        api.getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                setCareProviders(undefined);
            }
        }, undefined, () => {
            setIsFetchingCareProviders(false);
        });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemAdministrator) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
        }
    }

    const getEmergencyTreatmentStatuses = () => {
        api.getEmergencyTreatmentReportStatuses((statuses) => {
            const mappedStatuses = statuses.map<IListItem>((status) => { return { text: status.description, value: status.emergencyTreatmentReportStatusId.toString() } });
            setEmergencyTreatmentStatuses(mappedStatuses);
        });
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaCareProviderId) {
            return preSetSearchParams.umbrellaCareProviderId.toString();
        }

        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }

        }
        return undefined;
    }

    const exportSearchResultToExcel = (data: ISearchEmergencyTreatmentReport) => {
        setIsExportingToExcel(true);
        api.getFilteredEmergencyTreatmentReports(data, true, undefined, "Akuträkningar", undefined, () => setIsExportingToExcel(false));
    }

    return (
        <Form>
            <div className="row">
                <div className="col-6">
                    {
                        preSetSearchParams ?
                            <DatePickerRange label="Datum" name="fromDate" nameSecondary="toDate" inlineLabel
                                value={preSetSearchParams?.fromDate ? new Date(preSetSearchParams.fromDate) : undefined}
                                valueSecondary={preSetSearchParams?.toDate ? new Date(preSetSearchParams.toDate) : undefined} />
                            : null
                    }
                    <CareProviderDropdown
                        name="umbrellaCareProviderId"
                        label="Vårdgivare"
                        careProviders={umbrellaOrganisations}
                        onValueChange={onUmbrellaCareProviderChange}
                        defaultValue={getUmbrellaCareProviderDefaultValue()}
                        disabled={!hasRoleSystemAdministrator}
                        useFixedListItemHeight
                        showOrgNum
                        placeholder="Välj vårdgivare"
                        isClearable
                        isLoading={isFetchingUmbrellaOrganisations}
                    />
                    <CareProviderDropdown
                        name="careProviderId"
                        label="Mottagning"
                        careProviders={careProviders ?? []}
                        defaultValue={getCareProviderDefaultValue()}
                        disabled={!(hasRoleParaply || hasRoleSystemAdministrator)}
                        useFixedListItemHeight
                        showClinicId
                        isClearable
                        isLoading={isFetchingCareProviders}
                        clearValueIfNoInitalValue
                    />
                </div>
                <div className="col-6">
                    <Select name="emergencyTreatmentStatusId" label="Status" placeholder="Välj en status" options={emergencyTreatmentStatuses ?? []} selectedValue={preSetSearchParams?.emergencyTreatmentStatusId?.toString()} isLoading={emergencyTreatmentStatuses === undefined} inlineLabel isClearable />
                    <TextInput name="emergencyTreatmentNumber" label="Ärendenummer" defaultValue={preSetSearchParams?.emergencyTreatmentNumber} inlineLabel />
                    <Checkbox label="Visa ärenden äldre än tre månader" name="showOlderThan3Months" id="showOlderThan3Months" value="true" checked={preSetSearchParams?.showOlderThan3Months} />
                </div>
            </div>
            <div className="col-12">
                <div className="text-right panel">
                    <span className="d-none">
                        <CustomSubmitButton onButtonSubmit={searchEmergencyTreatments} >Sök</CustomSubmitButton>
                    </span>
                    <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={exportSearchResultToExcel} isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till excel</CustomSubmitButton>
                    <ClearFormButton />
                    <CustomSubmitButton id="submitbutton" onButtonSubmit={searchEmergencyTreatments}>Sök</CustomSubmitButton>
                </div>
            </div>
        </Form>
    )
}